import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Render a React subtree into ANOTHER window's document (ported from Relay's Interview Console,
 * RELAY-720). A Document PiP / popup window starts as a blank same-origin document with none of
 * the app's CSS, so before anything renders this copies, each in its own effect so one cannot
 * re-run another (a re-run stylesheet clone would append every sheet twice):
 *  1. the title, so the OS window has a name;
 *  2. the opener `<html>`'s `class` and `style`, MIRRORED for the window's life — the theme is the
 *     `dark` class (`applyTheme`), so a one-shot copy would leave the window on the old theme —
 *     and `<body>`'s class (`ds-v2`, which the `--v2-*` tokens need), re-copied with them;
 *  3. every `<link rel="stylesheet">` / `<style>` of the opener's head, one copy each. In development
 *     Vite injects CSS as `<style>` tags after first paint AND rewrites a tag in place on every
 *     edit (Tailwind's one `app.css`), so the head is watched and each copy kept in step: a stale
 *     copy lacks the classes added since the window opened.
 *
 * React events work in the portal (React attaches listeners to the portal container). Radix
 * portals do NOT: Dialog/Popover/Select/Tooltip portal to `document.body`, which from here is the
 * OPENER's body, so they would open in the main tab. The companion uses plain elements only.
 */
export function ExternalWindowPortal({
  win,
  title,
  children,
}: {
  win: Window | null;
  title: string;
  children: ReactNode;
}) {
  const [styled, setStyled] = useState(false);

  useEffect(() => {
    if (!win || win.closed) return;
    win.document.title = title;
  }, [win, title]);

  useEffect(() => {
    if (!win || win.closed) return;
    const src = document.documentElement;
    const dst = win.document.documentElement;
    const sync = () => {
      if (win.closed) return;
      dst.className = src.className;
      win.document.body.className = document.body.className;
      const style = src.getAttribute('style');
      if (style === null) dst.removeAttribute('style');
      else dst.setAttribute('style', style);
    };
    sync();
    win.document.body.style.margin = '0';
    const observer = new MutationObserver(sync);
    observer.observe(src, { attributes: true, attributeFilter: ['class', 'style'] });
    return () => observer.disconnect();
  }, [win]);

  useEffect(() => {
    if (!win || win.closed) {
      setStyled(false);
      return;
    }
    const doc = win.document;
    const copies = new Map<Element, Element>();
    const sources = () => document.head.querySelectorAll('link[rel="stylesheet"], style');
    const sync = () => {
      sources().forEach((node) => {
        const copy = copies.get(node);
        if (!copy) {
          const fresh = node.cloneNode(true) as Element;
          copies.set(node, fresh);
          doc.head.appendChild(fresh);
        } else if (node.tagName === 'STYLE' && copy.textContent !== node.textContent) {
          copy.textContent = node.textContent;
        }
      });
    };
    sync();
    let observer: MutationObserver | null = null;
    if (import.meta.env.DEV) {
      observer = new MutationObserver(() => {
        if (!win.closed) sync();
      });
      observer.observe(document.head, { childList: true, subtree: true, characterData: true });
    }
    setStyled(true);
    return () => observer?.disconnect();
  }, [win]);

  if (!win || win.closed || !styled) return null;
  return createPortal(
    <div className="bg-background text-foreground h-screen overflow-hidden font-sans">
      {children}
    </div>,
    win.document.body,
  );
}
